// The curated icon set a club can pick for a call-to-action button.
//
// Stored on cta_buttons.icon by key; rendered by the fan page and the phone
// preview through ctaIcon(). Line icons from the app's own icon library draw
// in the button's text colour, so they fit any club accent — unlike an
// uploaded bitmap. Keep the list short and obvious: a club admin scans it.
import type { LucideIcon } from 'lucide-react';
import {
  Beer, Bus, Calendar, Camera, Car, Clock, Coffee, Flag, Gift, Globe, Heart, Info, Mail, MapPin,
  Megaphone, Music, Newspaper, PartyPopper, Phone, Shirt, ShoppingBag, Star, Ticket, Trophy,
  Users, Utensils, Video, Wifi,
} from 'lucide-react';

export interface CtaIconEntry {
  key: string;
  label: string;
  Icon: LucideIcon;
}

export const CTA_ICONS: readonly CtaIconEntry[] = [
  { key: 'ticket', label: 'Tickets', Icon: Ticket },
  { key: 'shirt', label: 'Merchandise', Icon: Shirt },
  { key: 'shopping-bag', label: 'Shop', Icon: ShoppingBag },
  { key: 'utensils', label: 'Food', Icon: Utensils },
  { key: 'beer', label: 'Drinks', Icon: Beer },
  { key: 'coffee', label: 'Coffee', Icon: Coffee },
  { key: 'gift', label: 'Gift', Icon: Gift },
  { key: 'trophy', label: 'Trophy', Icon: Trophy },
  { key: 'star', label: 'Star', Icon: Star },
  { key: 'heart', label: 'Heart', Icon: Heart },
  { key: 'party-popper', label: 'Party', Icon: PartyPopper },
  { key: 'megaphone', label: 'Announcement', Icon: Megaphone },
  { key: 'newspaper', label: 'News', Icon: Newspaper },
  { key: 'music', label: 'Music', Icon: Music },
  { key: 'camera', label: 'Photos', Icon: Camera },
  { key: 'video', label: 'Video', Icon: Video },
  { key: 'users', label: 'Community', Icon: Users },
  { key: 'calendar', label: 'Calendar', Icon: Calendar },
  { key: 'clock', label: 'Schedule', Icon: Clock },
  { key: 'map-pin', label: 'Location', Icon: MapPin },
  { key: 'car', label: 'Parking', Icon: Car },
  { key: 'bus', label: 'Transport', Icon: Bus },
  { key: 'flag', label: 'Flag', Icon: Flag },
  { key: 'wifi', label: 'Wi-Fi', Icon: Wifi },
  { key: 'info', label: 'Info', Icon: Info },
  { key: 'phone', label: 'Phone', Icon: Phone },
  { key: 'mail', label: 'Email', Icon: Mail },
  { key: 'globe', label: 'Website', Icon: Globe },
];

const BY_KEY = new Map(CTA_ICONS.map((entry) => [entry.key, entry.Icon]));

export function isCtaIconKey(key: unknown): key is string {
  return typeof key === 'string' && BY_KEY.has(key);
}

/** The icon component for a stored key; null for an unknown or missing key. */
export function ctaIcon(key: string | null | undefined): LucideIcon | null {
  return isCtaIconKey(key) ? BY_KEY.get(key)! : null;
}
